import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Modal,
  rem,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import AssignOrgDrawer from "./AssignOrgDrawer";
import { Order } from "../../../../types/accountManagementTypes";
import { modals } from "@mantine/modals";
import { deleteLicenses, getInvoices, unassignLicenses } from "@utils/api-communication/RestApiCli";

export type SelectableLicenseListProps = {
  licenses?: LicenseAndProduct[];
  order?: Order;
  distId?: string | null;
  onDeleted?: () => void;
};

export type LicenseAndProduct = {
  _id?: any;
  licenseId: string;
  license_id: string;
  licenseNumber: number;
  type: string;
  state?: string;
  invalidAfter?: Date;
  history: any[];
  isActive: boolean;
  orgId?: string;
  pid?: string;
  invoiceNo?: string;
  distId?: string;
};

const SKU_LABELS: Record<string, string> = {
  HWP301: "VR Headset – Pico Neo 3 Pro Eye",
  HWS301: "VR Headset – Play for Dream S3",
  ACCS01: "VR Headset Travel Case",
  SWVF11: "Oculera Visual Field Analyzer Software v1.1",
  LCDE00: "Demo Software Licence",
  LCUP11: "Upgrade Licence (Demo → Full v1.1)",
  FULL01: "Full Commercial License",
  SWMN01: "Annual Software Maintenance Fee",
  FSSH01: "Shipping Fee",
  FSHD01: "Handling Fee",
  ML01: "Monthly License",
  ANS01: "Annual Subscription",
};

const formatAddress = (obj: any) => {
  if (!obj) return "-";
  if (typeof obj === "string") return obj;
  return (
    [obj.name, obj.address, obj.city, obj.country].filter(Boolean).join(", ") || JSON.stringify(obj)
  );
};

export default function SelectableLicenseList({
  licenses,
  order,
  distId,
  onDeleted,
}: SelectableLicenseListProps) {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [canUnassign, setCanUnassign] = useState(false);

  // Invoice popup state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  useEffect(() => {
    const selectedLicenses = (selectedRows || [])
      .map((index) => (licenses || [])[index])
      .filter(Boolean);
    const allActive = selectedLicenses.every((license) => license.isActive);
    const haveOrganization = selectedLicenses.every((license) => license.orgId);
    setCanUnassign(allActive && haveOrganization && selectedLicenses.length > 0);
  }, [selectedRows, licenses]);

  // Reset selection when the licenses list changes (e.g. page/filter change)
  useEffect(() => {
    setSelectedRows([]);
  }, [licenses]);

  const selectedLicenses = (selectedRows || [])
    .map((index) => (licenses || [])[index])
    .filter(Boolean);

  const getLicenseDistId = (license?: LicenseAndProduct | null) =>
    license?.distId ?? order?.distId ?? distId ?? null;

  const selectedDistId = getLicenseDistId(selectedLicenses[0]);
  const hasMixedDistIds =
    new Set((licenses || []).map((license) => getLicenseDistId(license)).filter(Boolean)).size > 1;

  const handleUnassign = () => {
    if (!canUnassign) return;
    const orgId = selectedLicenses[0].orgId as string;
    const licenseUuids = selectedLicenses.map((l) => l.licenseId).filter(Boolean);

    modals.open({
      title: "Unassign Licenses",
      children: (
        <>
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            You are about to unassign {licenseUuids.length} active license(s) from organization{" "}
            {orgId}. Continue?
          </div>
          <Flex gap="sm" justify="flex-end">
            <Button variant="default" onClick={() => modals.closeAll()} disabled={unassignLoading}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={unassignLoading}
              onClick={() => {
                setUnassignLoading(true);
                unassignLicenses(
                  { licenseUuids, orgId },
                  () => {
                    setUnassignLoading(false);
                    modals.closeAll();
                    setSelectedRows([]);
                  },
                  (err) => {
                    console.error(err);
                    setUnassignLoading(false);
                  },
                );
              }}
            >
              Unassign
            </Button>
          </Flex>
        </>
      ),
    });
  };

  const allSelected =
    (licenses?.length ?? 0) > 0 &&
    selectedRows.length ===
      (licenses || []).filter((license) => getLicenseDistId(license) === selectedDistId).length;
  const someSelected = selectedRows.length > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      const targetDistId = selectedDistId ?? getLicenseDistId((licenses ?? [])[0]);
      setSelectedRows(
        (licenses ?? [])
          .map((license, i) => ({ license, i }))
          .filter(({ license }) => getLicenseDistId(license) === targetDistId)
          .map(({ i }) => i),
      );
    }
  };

  const openDeleteConfirm = () => {
    const ids = selectedLicenses.map((l) => l._id).filter(Boolean);
    const count = ids.length;
    const deleteDistId = selectedDistId;
    if (!deleteDistId) {
      modals.open({
        title: "Missing Distributor",
        children: (
          <Stack gap="sm">
            <Text size="sm">
              These licenses do not have a distributor ID available, so they cannot be deleted.
            </Text>
            <Flex justify="flex-end">
              <Button variant="default" onClick={() => modals.closeAll()}>
                Close
              </Button>
            </Flex>
          </Stack>
        ),
      });
      return;
    }

    if (ids.length === 0) {
      modals.open({
        title: "Missing License IDs",
        children: (
          <Stack gap="sm">
            <Text size="sm">
              These licenses do not have `_id` values available, so they cannot be deleted.
            </Text>
            <Flex justify="flex-end">
              <Button variant="default" onClick={() => modals.closeAll()}>
                Close
              </Button>
            </Flex>
          </Stack>
        ),
      });
      return;
    }

    modals.openConfirmModal({
      title: "Delete Licenses",
      children: (
        <Stack gap={4}>
          <Text size="sm">
            You are about to permanently delete{" "}
            <Text span fw={700}>
              {count}
            </Text>{" "}
            license{count !== 1 ? "s" : ""}. This cannot be undone.
          </Text>
          <Text size="sm" c="dimmed">
            Distributor: {deleteDistId}
          </Text>
        </Stack>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteLicenses(
          ids,
          () => {
            setSelectedRows([]);
            onDeleted?.();
          },
          (err: any) =>
            console.error(
              err?.response?.data?.message || err?.message || "Failed to delete licenses",
            ),
          deleteDistId,
        );
      },
    });
  };

  const openInvoiceModal = (invoiceNo: string) => {
    setInvoiceData(null);
    setInvoiceError(null);
    setInvoiceLoading(true);
    setInvoiceModalOpen(true);

    getInvoices(
      { invoiceNo },
      (resp: any) => {
        const list = resp?.data ?? [];
        setInvoiceData(Array.isArray(list) ? (list[0] ?? null) : (list ?? null));
        setInvoiceLoading(false);
      },
      (err: any) => {
        setInvoiceError(err?.message || "Failed to load invoice");
        setInvoiceLoading(false);
      },
    );
  };

  const rows = (licenses || []).map((license, index) => {
    const rowDistId = getLicenseDistId(license);
    const isSelected = selectedRows.includes(index);
    const disabledByDistId = !!selectedDistId && rowDistId !== selectedDistId;

    return (
      <Table.Tr
        key={license?.licenseId || index}
        bg={isSelected ? "var(--mantine-color-red-0)" : undefined}
        opacity={disabledByDistId ? 0.55 : 1}
      >
        <Table.Td>
          <Checkbox
            aria-label="Select row"
            checked={isSelected}
            disabled={disabledByDistId}
            onChange={(event) =>
              setSelectedRows(
                event.currentTarget.checked
                  ? [...selectedRows, index]
                  : selectedRows.filter((position) => position !== index),
              )
            }
          />
        </Table.Td>
        <Table.Td>{license?.licenseNumber}</Table.Td>
        <Table.Td>{license?.licenseId}</Table.Td>
        <Table.Td>{license?.type}</Table.Td>
        <Table.Td>{license?.state}</Table.Td>
        <Table.Td>{license?.orgId ?? ""}</Table.Td>
        <Table.Td>
          {license?.invoiceNo ? (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => openInvoiceModal(license.invoiceNo!)}
              style={{ fontFamily: "monospace", padding: "0 4px" }}
            >
              {license.invoiceNo}
            </Button>
          ) : (
            <Text size="xs" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Button size="compact-xs" radius={"xl"} color={"orange"}>
            {license?.pid ?? " "} Pair
          </Button>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <AssignOrgDrawer
        opened={drawerOpened}
        close={closeDrawer}
        licenses={selectedRows.map((index) => (licenses || [])[index])}
        order={order as Order}
      />

      <Card withBorder radius="md" p="md" mih={rem(400)}>
        <Card.Section p="md">
          <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
            <Title order={4}>{order ? "Ordered Licenses" : "Licenses"}</Title>
            <Flex gap="sm" wrap="wrap">
              <Button size="sm" disabled={selectedRows.length === 0} onClick={openDrawer}>
                Assign License
              </Button>
              <Button
                size="sm"
                color="red"
                variant="outline"
                disabled={!canUnassign}
                onClick={handleUnassign}
              >
                Unassign Licenses
              </Button>
              <Button size="sm" color="red" disabled={!someSelected} onClick={openDeleteConfirm}>
                Delete selected ({selectedRows.length})
              </Button>
            </Flex>
          </Flex>
          {hasMixedDistIds && (
            <Text size="xs" c="dimmed" mt="xs">
              Only licenses from the same distributor can be selected together for deletion.
            </Text>
          )}
        </Card.Section>
        <Stack>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 36 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </Table.Th>
                <Table.Th>License Number</Table.Th>
                <Table.Th>License Id</Table.Th>
                <Table.Th>License Type</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Organization</Table.Th>
                <Table.Th>Invoice No.</Table.Th>
                <Table.Th>Device</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Stack>
      </Card>

      {/* Invoice detail popup */}
      <Modal
        opened={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        title={invoiceData ? `Invoice ${invoiceData.invoiceNo}` : "Invoice"}
        size="lg"
      >
        {invoiceLoading && (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        )}
        {invoiceError && (
          <Text size="sm" c="red">
            {invoiceError}
          </Text>
        )}
        {!invoiceLoading && !invoiceError && !invoiceData && (
          <Text size="sm" c="dimmed">
            Invoice not found.
          </Text>
        )}
        {!invoiceLoading && invoiceData && (
          <Stack gap="sm">
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600, width: 160 }}>Invoice No.</Table.Td>
                  <Table.Td style={{ fontFamily: "monospace" }}>{invoiceData.invoiceNo}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Distributor</Table.Td>
                  <Table.Td>{invoiceData.distId ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Date</Table.Td>
                  <Table.Td>{invoiceData.date ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>From</Table.Td>
                  <Table.Td>{formatAddress(invoiceData.invoiceFrom)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>To</Table.Td>
                  <Table.Td>{formatAddress(invoiceData.invoiceTo)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td style={{ fontWeight: 600 }}>Licenses Generated</Table.Td>
                  <Table.Td>
                    <Badge color={invoiceData.softLicensesGenerated ? "green" : "gray"}>
                      {invoiceData.softLicensesGenerated ? "Yes" : "No"}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
                {invoiceData.createdAt && (
                  <Table.Tr>
                    <Table.Td style={{ fontWeight: 600 }}>Created</Table.Td>
                    <Table.Td>{new Date(invoiceData.createdAt).toLocaleString()}</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>

            {!!invoiceData.items?.length && (
              <>
                <Divider />
                <Title order={6}>Items ({invoiceData.items.length})</Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }}>#</Table.Th>
                      <Table.Th>SKU</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 80 }}>Qty</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {invoiceData.items.map((item: any, idx: number) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{idx + 1}</Table.Td>
                        <Table.Td style={{ fontFamily: "monospace" }}>{item.sku}</Table.Td>
                        <Table.Td>{SKU_LABELS[item.sku] ?? item.sku}</Table.Td>
                        <Table.Td>{item.quantity}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </Stack>
        )}
        <Flex justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setInvoiceModalOpen(false)}>
            Close
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
